import {
  DriveAccessRoleEnum,
  WorkspaceType,
} from 'components/Workspaces/types';
import {
  Actions,
  Subjects,
  useIsEnterpriseOwnerOrAdmin,
  useIsRbacEnabled,
  useIsTeamMultiSeat,
  useUserAbilitiesContext,
} from 'context/user/UserContext';
import { useCanCreateProject } from './projects';
import { useCanCreateFolders } from './folders';

export const useCanReadTeamWorkspace = () => {
  const isRbacEnabled = useIsRbacEnabled();
  const ability = useUserAbilitiesContext();
  return isRbacEnabled
    ? ability.can(Actions.Read, Subjects.TeamWorkspace)
    : ability.can(Actions.View, Subjects.Workspace);
};

export const useCanReadCustomWorkspace = () => {
  const isRbacEnabled = useIsRbacEnabled();
  const ability = useUserAbilitiesContext();
  return isRbacEnabled
    ? ability.can(Actions.Read, Subjects.CustomWorkspace)
    : ability.can(Actions.View, Subjects.Workspace);
};

export const useCanCreateWorkspace = () => {
  const isRbacEnabled = useIsRbacEnabled();
  const ability = useUserAbilitiesContext();
  const isTeamUser = useIsTeamMultiSeat();
  const isEnterpriseOwnerOrAdmin = useIsEnterpriseOwnerOrAdmin();

  const canCreateWorkspaceLegacy = isTeamUser && isEnterpriseOwnerOrAdmin;
  const canCreateWorkspaceRbac = ability.can(
    Actions.Create,
    Subjects.CustomWorkspace
  );

  return isRbacEnabled ? canCreateWorkspaceRbac : canCreateWorkspaceLegacy;
};

export const useCanUpdateCustomWorkspace = ({
  driveAccessRole,
}: {
  driveAccessRole?: DriveAccessRoleEnum;
}) => {
  const isRbacEnabled = useIsRbacEnabled();
  const isEnterpriseAdminOrOwner = useIsEnterpriseOwnerOrAdmin();
  const ability = useUserAbilitiesContext();

  const hasFullCustomDriveAccess =
    driveAccessRole === DriveAccessRoleEnum.FullAccess;

  const hasUpdateCustomWorkspaceAccessLegacy =
    isEnterpriseAdminOrOwner || hasFullCustomDriveAccess;

  const hasUpdateCustomWorkspaceAccess =
    ability.can(Actions.Update, Subjects.CustomWorkspace) &&
    hasFullCustomDriveAccess;

  return isRbacEnabled
    ? hasUpdateCustomWorkspaceAccess
    : hasUpdateCustomWorkspaceAccessLegacy;
};

export const useCanCreateWorkspaceResources = (inputs?: {
  driveId?: string;
  driveAccess?: DriveAccessRoleEnum;
}) => {
  const { driveId, driveAccess } = inputs || {};
  const canCreateProjects = useCanCreateProject();
  const canCreateFolders = useCanCreateFolders();
  const canCreateWorkspace = useCanCreateWorkspace();

  if (driveAccess && driveAccess !== DriveAccessRoleEnum.FullAccess)
    return false;
  if (driveId && driveId.toLowerCase() === WorkspaceType.Shared.toLowerCase())
    return false;

  return canCreateWorkspace || canCreateFolders || canCreateProjects;
};

export default {
  useCanReadTeamWorkspace,
  useCanReadCustomWorkspace,
  useCanCreateWorkspace,
  useCanUpdateCustomWorkspace,
  useCanCreateWorkspaceResources,
};
